import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "@/Components/ui";;
import toast, { Toaster } from 'react-hot-toast';
import Select from "react-select";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import ServerPaginatedTable from "../../Components/Common/ServerPaginatedTable";
import { useSenders } from "../../hooks/useMessaging";
import { siteService } from "../../api/services/site.service";
import { SITE_ID, SUBDOMAIN } from "../../assets/meta/meta";

import { TEMPLATE_CATEGORIES, CATEGORY_GROUPS } from "../../utils/kakao_template_category";

const SenderManagement = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState({
    id: SITE_ID, subdomain: SUBDOMAIN
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [modal, setModal] = useState(false);
  const [editingSender, setEditingSender] = useState(null);
  const [formData, setFormData] = useState({
    profile_name: '',
    business_name: '',
    business_number: '',
    category_code: '008001',
  });

  const {
    senders,
    pagination,
    loading,
    error,
    fetchSenders,
    createSender,
    updateSender,
    syncSenderStatus,
  } = useSenders();

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSitesApp();
        setSites(response.data.result.sites);
      } catch (error) {
        console.error("Failed to fetch sites:", error);
        toast.error("사이트 목록을 불러오는데 실패했습니다.");
      }
    };

    fetchSites();
  }, []);

  const handleFilterChange = () => {
    const filters = {};

    if (selectedSite) filters.site_id = selectedSite.id;
    if (statusFilter) filters.status = statusFilter;
    if (searchTerm) filters.search = searchTerm;

    fetchSenders(filters);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingSender(null);
      setFormData({
        profile_name: '',
        business_name: '',
        business_number: '',
        category_code: '008001',
      });
    }
  };

  const handleEdit = (sender) => {
    setEditingSender(sender);
    setFormData({
      profile_name: sender.profile_name,
      business_name: sender.business_name,
      business_number: sender.business_number || '',
      category_code: sender.category_code || '008001',
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSite) {
      toast.error("사이트를 선택해주세요.");
      return;
    }

    try {
      const data = {
        ...formData,
        site_id: selectedSite.id,
      };

      if (editingSender) {
        await updateSender(editingSender.id, data);
        toast.success("발신자 정보가 수정되었습니다.");
      } else {
        await createSender(data);
        toast.success("발신자가 생성되었습니다.");
      }

      toggleModal();
      handleFilterChange();
    } catch (error) {
      toast.error(error.message || "발신자 저장에 실패했습니다.");
    }
  };

  const handleSyncStatus = async (senderId) => {
    try {
      await syncSenderStatus(senderId);
      toast.success("발신자 상태가 동기화되었습니다.");
      handleFilterChange();
    } catch (error) {
      toast.error("상태 동기화에 실패했습니다.");
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'pending': { label: '승인대기', color: 'warning' },
      'active': { label: '활성', color: 'success' },
      'inactive': { label: '비활성', color: 'secondary' },
      'blocked': { label: '차단', color: 'danger' }
    };
    const statusInfo = statuses[status] || { label: status, color: 'secondary' };
    return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      enableColumnFilter: false,
      size: 80,
    },
    {
      Header: "사이트",
      accessor: "site",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const site = sites.find(s => s.id === row.original.site);
        return site ? site.name : row.original.site;
      },
    },
    {
      Header: "프로필명",
      accessor: "profile_name",
      enableColumnFilter: false,
    },
    {
      Header: "사업자명",
      accessor: "business_name",
      enableColumnFilter: false,
    },
    {
      Header: "발신자 키",
      accessor: "send_profile_key",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <code className="text-muted small">
          {row.original.send_profile_key}
        </code>
      ),
    },
    {
      Header: "상태",
      accessor: "status",
      enableColumnFilter: false,
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 100,
    },
    {
      Header: "생성일",
      accessor: "created_at",
      enableColumnFilter: false,
      cell: ({ row }) => formatDateTime(row.original.created_at),
      size: 150,
    },
    {
      Header: "작업",
      accessor: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" className="btn btn-soft-secondary btn-sm">
            <i className="ri-more-fill"></i>
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-end">
            <DropdownItem onClick={() => handleEdit(row.original)}>
              <i className="ri-pencil-line align-bottom me-2 text-muted"></i>
              수정
            </DropdownItem>
            <DropdownItem onClick={() => handleSyncStatus(row.original.id)}>
              <i className="ri-refresh-line align-bottom me-2 text-muted"></i>
              상태 동기화
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => console.log('View sender:', row.original)}>
              <i className="ri-eye-line align-bottom me-2 text-muted"></i>
              상세보기
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      size: 100,
    },
  ];

  const siteOptions = sites.map(site => ({
    value: site.id,
    label: `${site.name} (${site.subdomain})`,
    site: site,
  }));

  const statusOptions = [
    { value: 'pending', label: '승인대기' },
    { value: 'active', label: '활성' },
    { value: 'inactive', label: '비활성' },
    { value: 'blocked', label: '차단' }
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="발신자 관리" pageTitle="메시징" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="card-title mb-0">카카오 발신자 관리</h5>
                    </Col>
                    <Col className="col-auto">
                      <Button color="primary" size="sm" onClick={toggleModal}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        발신자 추가
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Select
                        value={siteOptions.find(option => option.value === selectedSite?.id)}
                        onChange={(option) => setSelectedSite(option?.site || null)}
                        options={siteOptions}
                        placeholder="사이트 선택"
                        // isClearable
                        isDisabled
                      />
                    </Col>
                    <Col md={3}>
                      <Select
                        value={statusOptions.find(option => option.value === statusFilter)}
                        onChange={(option) => setStatusFilter(option?.value || '')}
                        options={statusOptions}
                        placeholder="상태"
                        isClearable
                      />
                    </Col>
                    <Col md={3}>
                      <Input
                        type="text"
                        placeholder="프로필명 검색"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button color="primary" onClick={handleFilterChange} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : <i className="ri-search-line"></i>}
                      </Button>
                    </Col>
                  </Row>

                  {error && (
                    <div className="alert alert-danger">
                      <strong>오류:</strong> {error}
                    </div>
                  )}

                  <ServerPaginatedTable
                    columns={columns}
                    data={senders || []}
                    previous={pagination?.previous}
                    next={pagination?.next}
                    count={pagination?.count || 0}
                    onRequestData={(url) => {
                      const urlParams = new URL(url, window.location.origin);
                      const page = urlParams.searchParams.get('page');
                      const filters = {};

                      if (selectedSite) filters.site_id = selectedSite.id;
                      if (statusFilter) filters.status = statusFilter;
                      if (searchTerm) filters.search = searchTerm;
                      if (page) filters.page = page;

                      fetchSenders(filters);
                    }}
                    loading={loading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} onClose={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {editingSender ? '발신자 수정' : '발신자 추가'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>프로필명 *</Label>
              <Input
                type="text"
                value={formData.profile_name}
                onChange={(e) => setFormData({ ...formData, profile_name: e.target.value })}
                placeholder="발신자 프로필명"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>사업자명 *</Label>
              <Input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="사업자명"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>사업자등록번호</Label>
              <Input
                type="text"
                value={formData.business_number}
                onChange={(e) => setFormData({ ...formData, business_number: e.target.value })}
                placeholder="123-45-67890"
              />
            </FormGroup>
            <FormGroup>
              <Label>카테고리 코드</Label>
              <Input
                type="select"
                value={formData.category_code}
                onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
              >
                {Object.entries(CATEGORY_GROUPS).map(([groupName, categories]) => (
                  <optgroup key={groupName} label={groupName}>
                    {categories.map((category) => (
                      <option key={category.code} value={category.code}>
                        {category.code} - {category.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Input>
              <div className="text-muted small mt-1">
                선택된 카테고리: {TEMPLATE_CATEGORIES[formData.category_code]?.name || '알 수 없음'}
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={toggleModal}>
              취소
            </Button>
            <Button type="submit" color="primary" disabled={loading}>
              {loading && <Spinner size="sm" className="me-1" />}
              {editingSender ? '수정' : '생성'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Toaster position="top-center" />
    </React.Fragment>
  );
};

export default SenderManagement;